import React, { useMemo } from "react";

import { faArrowTurnLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import { ColumnsType } from "antd/es/table";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { differenceInDays } from "date-fns/esm";
import { useIntl } from "react-intl";

import ClientType from "@mapmycustomers/shared/enum/ClientType";
import Located from "@mapmycustomers/shared/types/base/Located";
import {
  Activity,
  Deal,
  EntitySupportingRecordRestoration,
  EntityType,
  EntityTypesSupportingRestoration,
} from "@mapmycustomers/shared/types/entity";
import { Company } from "@mapmycustomers/shared/types/entity";
import { Person } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { isNotEmptyString } from "@mapmycustomers/shared/util/assert";
import { AvatarWithName, Button, Popconfirm } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import Chip from "@app/component/Chip";
import getFormattedDate from "@app/util/activity/getFormattedDate";
import { formatRawDate, getFormattedAddressForUi } from "@app/util/formatters";
import locatedToAddress from "@app/util/locatedToAddress";
import { parseApiDate } from "@app/util/parsers";
import { getClientTypeDisplayName } from "@app/util/ui";

import { DELETED_RECORDS_RESTORABLE_DAYS_LIMIT } from "./consts";
import messages from "./messages";
import styles from "./useColumns.module.scss";

const deletedAtComparator = (
  a: EntitySupportingRecordRestoration,
  b: EntitySupportingRecordRestoration
) => (a.deletedAt && b.deletedAt ? a.deletedAt.localeCompare(b.deletedAt) : 0);

const useColumns = (
  disableRestoreButton: boolean,
  onRestoreRecords: (payload: {
    entityType: EntityTypesSupportingRestoration;
    ids: EntitySupportingRecordRestoration["id"][];
  }) => void,
  allUsers: User[]
) => {
  const intl = useIntl();

  const columns: ColumnsType<EntitySupportingRecordRestoration> = useMemo(
    () => [
      {
        dataIndex: "name",
        key: "name",
        render: (_, record) => {
          let info: JSX.Element | string | undefined;
          if ([EntityType.COMPANY, EntityType.PERSON].includes(record.entity)) {
            const companyOrPerson = record as Company | Person;
            info = [
              getFormattedAddressForUi(locatedToAddress(companyOrPerson as Located)),
              "primaryAccount" in companyOrPerson ? companyOrPerson.primaryAccount?.name : "",
              companyOrPerson.email,
            ]
              .filter(isNotEmptyString)
              .join(" • ");
          } else if (record.entity === EntityType.DEAL) {
            const deal = record as Deal;
            info = (
              <>
                <Badge className={styles.badge} color={deal.stage.color} text={deal.stage.name} />
                {` • ${[deal.funnel.name, deal.account?.name]
                  .filter(isNotEmptyString)
                  .join(" • ")}`}
              </>
            );
          } else if (record.entity === EntityType.ACTIVITY) {
            const activity = record as Activity;
            info = (
              <>
                <span className={styles.activityType}>
                  <ActivityTypeIcon activityType={activity.crmActivityType} size="sm" />
                  {activity.crmActivityType.name}
                </span>
                {` • ${[getFormattedDate(intl, activity), activity.account?.name]
                  .filter(isNotEmptyString)
                  .join(" • ")}`}
              </>
            );
          }
          return (
            <div className={styles.name}>
              <span>{record.name}</span>
              <Tooltip overlayClassName={styles.tooltip} title={info} trigger="click">
                <span className={styles.info}>{info}</span>
              </Tooltip>
            </div>
          );
        },
        title: intl.formatMessage({
          id: "settings.restorableRecords.table.columns.name",
          defaultMessage: "Record Name",
          description: "Record name column",
        }),
        width: "35%",
      },
      {
        dataIndex: "deletedBy",
        key: "deletedBy",
        render: (_, record) => {
          const deletedBy = allUsers.find(({ id }) => id === record.deletedBy);
          return (
            <div className={styles.deletedBy}>
              <span>
                {record.sourceUpdated
                  ? getClientTypeDisplayName(intl, record.sourceUpdated.clientType)
                  : ""}
              </span>
              {record.sourceUpdated &&
                [ClientType.MOBILE, ClientType.WEB].includes(record.sourceUpdated?.clientType) && (
                  <span className={styles.avatar}>
                    {intl.formatMessage(
                      {
                        id: "settings.restorableRecords.table.columns.deletedBy.user",
                        defaultMessage: "By {userTag}",
                        description: "Deleted By user tag",
                      },
                      {
                        userTag: (
                          <Chip inline>
                            <AvatarWithName size="xs" user={deletedBy ?? record.user} />
                          </Chip>
                        ),
                      }
                    )}
                  </span>
                )}
            </div>
          );
        },
        title: intl.formatMessage({
          id: "settings.restorableRecords.table.columns.deletedBy",
          defaultMessage: "Deleted By",
          description: "Deleted By column",
        }),
        width: "15%",
      },
      {
        dataIndex: "deletedAt",
        key: "deletedAt",
        render: (deletedAt) => formatRawDate(deletedAt, "PPPp"),
        sorter: deletedAtComparator,
        title: intl.formatMessage({
          id: "settings.restorableRecords.table.columns.deletedAt",
          defaultMessage: "Deletion Time",
          description: "Deleted At column",
        }),
        width: "25%",
      },
      {
        dataIndex: "daysLeft",
        key: "daysLeft",
        render: (_, { deletedAt }) =>
          intl.formatMessage(
            {
              id: "settings.restorableRecords.table.columns.daysLeft.count",
              defaultMessage: "{count} {count, plural, one {day} other {days}}",
              description: "Days left to restore count column",
            },
            {
              count: deletedAt
                ? DELETED_RECORDS_RESTORABLE_DAYS_LIMIT -
                  differenceInDays(new Date(), parseApiDate(deletedAt))
                : "",
            }
          ),
        title: intl.formatMessage({
          id: "settings.restorableRecords.table.columns.daysLeft",
          defaultMessage: "Days Left",
          description: "Days left to restore column",
        }),
      },
      {
        key: "restore",
        render: (_, { id, entity }) => (
          <Popconfirm
            cancelText={intl.formatMessage(messages.cancel)}
            description={intl.formatMessage(messages.confirmTitle)}
            disabled={disableRestoreButton}
            okText={intl.formatMessage(messages.restore)}
            onConfirm={() => onRestoreRecords({ entityType: entity, ids: [id] })}
          >
            <Button
              disabled={disableRestoreButton}
              icon={
                <FontAwesomeIcon
                  className={cn(styles.restore, { [styles.disabledIcon]: disableRestoreButton })}
                  icon={faArrowTurnLeft}
                />
              }
              iconOnly
              type="link"
            />
          </Popconfirm>
        ),
        title: intl.formatMessage({
          id: "settings.restorableRecords.table.columns.restore",
          defaultMessage: "Restore",
          description: "Restore column",
        }),
        width: "6rem",
      },
    ],
    [allUsers, disableRestoreButton, intl, onRestoreRecords]
  );

  return columns;
};

export default useColumns;
